import { useEffect, useState } from "react";
import { api, euro } from "../lib/api";
import { Loader2, MessageCircle, Phone, Mail } from "lucide-react";

const n = (x) => Number(x || 0).toLocaleString("it-IT");
const dayLabel = (d) => new Date(d + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
const monthLabel = (m) => new Date(m + "-01T00:00:00").toLocaleDateString("it-IT", { month: "short", year: "2-digit" });

function Kpi({ k, v, sub, testid }) {
  return (
    <div data-testid={testid} className="p-5 bg-white border border-border rounded-2xl">
      <div className="text-xs uppercase tracking-wider text-ink-soft">{k}</div>
      <div className="font-serif text-4xl mt-1">{n(v)}</div>
      {sub && <div className="text-xs text-ink-soft mt-1">{sub}</div>}
    </div>
  );
}

function Bars({ data, xKey, label, testid }) {
  const max = Math.max(...data.map((d) => d.views), 1);
  return (
    <div data-testid={testid} className="bg-white border border-border rounded-2xl p-5 md:p-6">
      <h3 className="font-serif text-2xl mb-4">{label}</h3>
      {!data.length ? <div className="text-sm text-ink-soft">Nessun dato ancora</div> : (
        <div className="flex items-end gap-1 h-40 overflow-x-auto">
          {data.map((d) => (
            <div key={d[xKey]} className="flex-1 min-w-[18px] max-w-[48px] flex flex-col items-center gap-1 group" title={`${d[xKey]}: ${d.views} visite · ${d.visitors} visitatori`}>
              <div className="w-full bg-ink rounded-t-md transition-opacity group-hover:opacity-70" style={{ height: `${(d.views / max) * 120}px` }} />
              <div className="text-[9px] text-ink-soft rotate-0 whitespace-nowrap">{xKey === "day" ? dayLabel(d.day) : monthLabel(d.month)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Table({ title, rows, cols, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-border rounded-2xl p-5 md:p-6">
      <h3 className="font-serif text-2xl mb-4">{title}</h3>
      {!rows.length ? <div className="text-sm text-ink-soft">Nessun dato ancora</div> : (
        <div className="space-y-2 text-sm">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-3 border-b border-border/60 pb-2">
              <div className="min-w-0 flex-1">{cols[0](r)}</div>
              <div className="text-right whitespace-nowrap font-medium">{cols[1](r)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [a, setA] = useState(null);
  useEffect(() => { setA(null); api.get(`/admin/analytics?days=${days}`).then((r) => setA(r.data)); }, [days]);
  if (!a) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin" /></div>;
  const c = a.clicks;
  return (
    <div data-testid="analytics-panel" className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-ink-soft mr-2">Periodo</span>
        {[7, 30, 90].map((d) => (
          <button key={d} data-testid={`analytics-range-${d}`} onClick={() => setDays(d)}
            className={`px-4 py-2 rounded-full text-sm border ${days === d ? "bg-ink text-white border-ink" : "bg-white border-border hover:bg-secondary"}`}>{d} giorni</button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi testid="kpi-total-views" k="Visite totali" v={a.total_views} sub={`${n(a.period_views)} negli ultimi ${days} gg`} />
        <Kpi testid="kpi-unique" k="Visitatori unici" v={a.unique_total} sub={`${n(a.unique_period)} negli ultimi ${days} gg`} />
        <Kpi testid="kpi-today" k="Visite oggi" v={a.today_views} sub={`${n(a.today_unique)} visitatori`} />
        <Kpi testid="kpi-quotes" k="Preventivi ricevuti" v={a.quotes.total} sub={`${n(a.quotes.period)} negli ultimi ${days} gg`} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[["click_whatsapp", "Clic WhatsApp", MessageCircle], ["click_phone", "Clic telefono", Phone], ["click_email", "Clic e-mail", Mail]].map(([k, label, Icon]) => (
          <div key={k} data-testid={`kpi-${k}`} className="p-5 bg-white border border-border rounded-2xl flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-secondary flex items-center justify-center"><Icon size={18} strokeWidth={1.5} /></div>
            <div><div className="text-xs uppercase tracking-wider text-ink-soft">{label}</div>
              <div className="font-serif text-3xl">{n(c[k].total)} <span className="text-sm text-ink-soft font-sans">· {n(c[k].period)} nel periodo</span></div></div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Bars testid="chart-daily" data={a.daily} xKey="day" label={`Visite giornaliere (${days} gg)`} />
        <Bars testid="chart-monthly" data={a.monthly} xKey="month" label="Visite mensili (12 mesi)" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Table testid="table-vehicles" title="Autovetture più viste" rows={a.top_vehicles}
          cols={[(r) => (
            <div className="flex items-center gap-3 min-w-0">
              {r.vehicle?.images?.[0] && <img src={r.vehicle.images[0]} alt="" className="w-14 h-10 object-cover rounded-lg shrink-0" />}
              <div className="min-w-0"><div className="truncate">{r.vehicle ? `${r.vehicle.make} ${r.vehicle.model}` : "Vettura rimossa"}</div>
                <div className="text-xs text-ink-soft truncate">{r.vehicle?.version} {r.vehicle ? `· ${euro(r.vehicle.price)}` : ""}</div></div>
            </div>), (r) => `${n(r.views)} viste · ${n(r.visitors)} utenti`]} />
        <div className="space-y-4">
          <Table testid="table-pages" title="Pagine più visualizzate" rows={a.pages}
            cols={[(r) => ({ "/": "Home / Catalogo", "/privacy-policy": "Privacy Policy", "/cookie-policy": "Cookie Policy" }[r.path] || r.path), (r) => `${n(r.views)} viste`]} />
          <Table testid="table-sources" title="Provenienza del traffico" rows={a.sources}
            cols={[(r) => r.source, (r) => `${n(r.views)} visite · ${n(r.visitors)} utenti`]} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Kpi k="Percorsi avviati" v={a.quotes.started} sub={`ultimi ${days} gg`} />
        <Kpi k="Appuntamenti" v={a.quotes.appointments} />
        <Kpi k="Videochiamate" v={a.quotes.videocalls} />
        <Kpi k="Blocchi vettura" v={a.quotes.blocks} />
        <Kpi k="Da mobile" v={a.devices.Mobile || 0} sub={`${n(a.devices.Desktop || 0)} da desktop`} />
      </div>
    </div>
  );
}
